import React, { Fragment, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import HeaderNew from "../Header/HeaderNew";
import Footer from "../Footer/Footer";
import NewsletterSection from "../UI/NewsletterSection/NewsletterSection";
import PrivacyNotice from "../UI/PrivacyNotice/PrivacyNotice";
import Routers from "../../routers/Routers";
import "../../styles/stickybar.css";
import "../../styles/ourFleet.css";
import LowercaseRedirect from "../LowercaseRedirect";
import { trackWhatsappClick, trackPhoneClick } from "../../SharedComponent/tracking";
import usePrefetchRoutes from "../../hooks/usePrefetchRoutes";

const Layout = () => {

  // WhatsApp and call buttons appear in the header, the footer, the floating
  // action button and inside several page sections. Rather than wiring an
  // onClick into each one — and having to remember for every new one — a single
  // delegated listener catches any click that lands on a wa.me / whatsapp or
  // tel: link anywhere in the app. `capture` so it still fires if the handler
  // on the link itself navigates away.
  useEffect(() => {
    const onClick = (e) => {
      const link = e.target?.closest?.("a[href]");
      if (!link) return;
      const href = link.getAttribute("href") || "";
      const where = link.closest("header") ? "header"
        : link.closest("footer") ? "footer"
        : link.closest("[class*='float'], [class*='sticky']") ? "floating_button"
        : "page";
      if (/wa\.me|api\.whatsapp\.com|whatsapp/i.test(href)) trackWhatsappClick(where);
      else if (/^tel:/i.test(href)) trackPhoneClick(where);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
  const [isCollapse, setIsCollapse] = useState(false);

  // ── Keep the WhatsApp FAB off the content it would otherwise cover ────────
  // A fixed circle over a single-column mobile grid will always end up on top
  // of *some* card: measured at 320-430px it sat on the Clio and 208 card
  // images, names and prices at 7 of 12 width/language combinations.
  //
  // Rather than guessing a "safe" corner that does not exist, the button asks
  // the page what is underneath it: if its own rect covers a vehicle image,
  // name, price or Book Now control, it slides mostly off the trailing edge
  // (still tappable, still discoverable); when nothing important is beneath
  // it, it sits fully visible. It also tucks while scrolling so it never
  // drags across the cars.
  //
  // Transform-only, and never on first paint, so it contributes no CLS.
  const [fabTucked, setFabTucked] = useState(false);
  useEffect(() => {
    if (!window.matchMedia("(max-width: 767.98px)").matches) return;

    const wouldCover = () => {
      const fab = document.querySelector(".rf-whatsapp-fab");
      if (!fab) return false;
      // Measure the UNTUCKED footprint, always. Reading the live rect while
      // the button is already tucked returns the shifted box, which then
      // reports "clear", which untucks it back onto the content — an
      // oscillation. The resting geometry is the honest question.
      const f = fab.getBoundingClientRect();
      const tucked = fab.classList.contains("rf-whatsapp-fab--tucked");
      const rtl = getComputedStyle(fab).direction === "rtl";
      const shift = tucked ? f.width * 0.62 * (rtl ? -1 : 1) : 0;
      const box = {
        left: f.left - shift, right: f.right - shift,
        top: f.top, bottom: f.bottom,
      };
      const hits = (r) =>
        !(r.right <= box.left + 2 || r.left >= box.right - 2 ||
          r.bottom <= box.top + 2 || r.top >= box.bottom - 2);
      // Any vehicle card on any surface, plus its meaningful parts.
      const cards = document.querySelectorAll(".rf-sc, .rf-car, [class*='card']");
      for (const c of cards) {
        const parts = [
          c.querySelector("img"),
          c.querySelector("[class*=price]"),
          c.querySelector("[class*=cta], [class*=book]"),
          c.querySelector("h2, h3, [class*=title], [class*=name]"),
        ];
        for (const el of parts) {
          if (el && hits(el.getBoundingClientRect())) return true;
        }
      }
      return false;
    };

    let idle;
    const settle = () => setFabTucked(wouldCover());
    const onScroll = () => {
      setFabTucked(true); // never drag across the cars
      clearTimeout(idle);
      idle = setTimeout(settle, 300);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Decide the resting state once the first paint has settled.
    const boot = setTimeout(settle, 1200);
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(idle);
      clearTimeout(boot);
    };
  }, []);

  // Warm the Fleet/Offers/CarDetails chunks once the browser is idle.
  usePrefetchRoutes();
  const location = useLocation();
  const isKycRoute = /^\/(?:[a-z]{2}\/)?kyc(?:\/|$)/i.test(location.pathname || "");

  // ── The Control Center is not a marketing page ──────────────────────────
  //
  // `/ops` is mounted outside the /:lang tree (routers/Routers.js) and is a
  // separate product: staff-only, noindex, its own visual language, its own
  // internal locale. It must NOT be wrapped in the public chrome.
  //
  // This was a real production bug, not a cosmetic one. The public header
  // carries a language switcher whose handler REWRITES THE PATH to add a
  // language prefix (HeaderNew.jsx handleLanguageChange -> /ops becomes
  // /fr/ops). nginx only serves a strict allow-list of public paths after a
  // language prefix, so /fr/ops matched `location ~ ^/(en|fr|ar)/` and hit
  // `try_files $uri =404` — an owner switching to French inside the console
  // was thrown out to the public 404 page.
  //
  // Hiding the chrome removes the broken control entirely; the console
  // renders its own EN/FR/AR switch that changes app state and never touches
  // the URL. The header/footer were also never wanted here visually — they
  // pushed the console's fixed sidebar down and added a customer newsletter
  // and WhatsApp button to an employee's screen.
  const isOpsRoute = /^\/ops(?:\/|$)/i.test(location.pathname || "");

  // Kept as one flag: every surface below is "public chrome", and a future
  // surface added to this component should default to being hidden on /ops
  // rather than having to remember a second condition.
  const hideChrome = isKycRoute || isOpsRoute;

  return (
    <Fragment>
      {!hideChrome && <HeaderNew />}
      {/* Portal target for the hero search form's date/time pickers (see
          CustomDateTimePicker3.js / DropoffDateTimePicker.js). Placed here —
          right after the header, near the top of the DOM — rather than at
          document.body: a portal appended at the very end of <body> sits
          after the footer, and focusing something inside it used to drag the
          whole page down to the bottom. Being a sibling of the header, with
          no ancestor `overflow: hidden` or low stacking context in between,
          it can also render above the header instead of being clipped by the
          hero section or capped by the hero card's z-index. */}
      <div id="rf-datepicker-portal" />
      {/* <main> landmark: the page had no main region, so assistive tech had no
          way to skip the header and jump straight to the page content. */}
      <main id="main-content" className="overflow-x-hidden main-content-wrapper rf-main">
        <LowercaseRedirect>
          <Routers />
        </LowercaseRedirect>
        {!hideChrome && <div className={isCollapse ? "d-none" : "sticky-media-bar"}>
          <div className="social-share-container --chatbot-sticky-bar-- d-none">
            <div
              className="icons share icon-canter social-share-side"
              onClick={() => {
                setIsCollapse(true);
              }}
            >
              <i className="fas fa-share social-share-side"></i>{" "}
            </div>
          </div>
        </div>}
        {!hideChrome && <div
          className={
            isCollapse ? "sticky-media-bar d-block" : " sticky-media-bar d-none"
          }
        >
          <div className="social-container">
            <div
              className="icons share icon-canter bg-secondary p-sm-0  social-share-side"
              onClick={() => {
                setIsCollapse(false);
              }}
            >
              <i className="ri-close-line"></i>{" "}
            </div>
            <div className="icons twitter icon-canter">
              {/* <a href="" aria-label="Twitter "> */}
                <i className="fab fa-twitter"></i>
              {/* </a> */}
            </div>
            <div className="icons github  icon-canter">
              {/* <a className href="" aria-label=""> */}
                <i className="fab fa-github "></i>
              {/* </a> */}
            </div>
            <div className="icons linkedin  icon-canter">
              {/* <a href="" aria-label="Linkedin -"> */}
                <i className="fab fa-linkedin-in "></i>
              {/* </a> */}
            </div>
            <div className="icons facebook icon-canter">
              {/* <a href="" aria-label="Facebook - "> */}
                <i className="fab fa-facebook"></i>
              {/* </a> */}
            </div>
            <div className="icons reddit icon-canter">
              {/* <a href="" aria-label="Reddit - "> */}
                <i className="fab fa-reddit  "></i>
              {/* </a> */}
            </div>
          </div>
        </div>}
      </main>
      {!hideChrome && <NewsletterSection />}
      {!hideChrome && <PrivacyNotice />}
      {!hideChrome && <Footer />}

      {/* Floating WhatsApp chat button — bottom-right on every page */}
      {!hideChrome && (
        <a
          href="https://api.whatsapp.com/send/?phone=212655585859&text=Hello+Route+Facile%21+I+would+like+information+about+car+rental.&type=phone_number&app_absent=0"
          target="_blank"
          rel="noopener noreferrer"
          className={`rf-whatsapp-fab${fabTucked ? " rf-whatsapp-fab--tucked" : ""}`}
          aria-label="Chat on WhatsApp"
        >
          <i className="fa-brands fa-whatsapp"></i>
        </a>
      )}
    </Fragment>
  );
};

export default Layout;
